import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import ListViewSettings from "../../components/ListViewSettings";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import "../../button.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllWorkSchedules, deleteWorkSchedule } from "../../services/WorkScheduleServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildWorkScheduleQueryParams } from "../../utils/listViewDateRanges";
import moment from 'moment';

function WorkScheduleTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const left_pinned = ["schedule_no", "delivery_date"];
    const right_pinned = ["id"];

    const sumQty = (records, field) =>
        records.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const qtyTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.delivery_date === todayDateLabel
        );
        return {
            scheduled_qty: sumQty(data.records, 'scheduled_qty'),
            supplied_qty: sumQty(data.records, 'supplied_qty'),
            accepted_qty: sumQty(data.records, 'accepted_qty'),
            today_scheduled_qty: sumQty(todayRecords, 'scheduled_qty'),
        };
    }, [data.records, todayDateLabel]);

    const formatQty = (value) => Number(value || 0).toFixed(2);

    const todaySchCard = useMemo(() => ({
        label: 'Today Sch.',
        value: formatQty(qtyTotals.today_scheduled_qty),
        gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #5EC8F0 100%)',
        shadow: '0 8px 20px rgba(0, 144, 216, 0.28)',
    }), [qtyTotals.today_scheduled_qty]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Scheduled',
            value: formatQty(qtyTotals.scheduled_qty),
            gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
        {
            label: 'Delivered',
            value: formatQty(qtyTotals.supplied_qty),
            gradient: 'linear-gradient(135deg, #305870 0%, #186098 55%, #5AA8D8 100%)',
            shadow: '0 8px 20px rgba(48, 88, 112, 0.24)',
        },
        {
            label: 'Accepted',
            value: formatQty(qtyTotals.accepted_qty),
            gradient: 'linear-gradient(135deg, #0E6A98 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
    ]), [qtyTotals]);

    const columns = useMemo(
        () => [
            { fieldName: 'plant', headerName: 'Plant', size: 65 },
            { fieldName: 'schedule_no', headerName: 'Job No', size: 72 },
            { fieldName: 'delivery_date', headerName: 'Del Date', size: 86 },
            { fieldName: 'delivery_time', headerName: 'Del Time', size: 72 },
            { fieldName: 'customer', headerName: 'Customer', size: 120 },
            { fieldName: 'consignee', headerName: 'Site/Project', size: 120 },
            { fieldName: 'scheduled_qty', headerName: 'Sch Qty', size: 72, align: 'right' },
            { fieldName: 'supplied_qty', headerName: 'Del Qty', size: 72, align: 'right' },
            { fieldName: 'accepted_qty', headerName: 'Acc Qty', size: 72, align: 'right' },
            { fieldName: 'balance_qty', headerName: 'Bal Qty', size: 72, align: 'right' },
            { fieldName: 'grade', headerName: 'Grade', size: 75 },
            { fieldName: 'delivery_mode', headerName: 'Delivery', size: 82 },
            { fieldName: 'salesrep_alias', headerName: 'CSM', size: 72 },
            { fieldName: 'concrete_structure', headerName: 'Structure', size: 95 },
            { fieldName: 'status', headerName: 'Status', size: 88 },
            { fieldName: 'status_2', headerName: 'Rec Status', size: 82 },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const onEditWorkSchedule = (workscheduleId) => {
        navigate(`/WorkScheduleEdit/${workscheduleId}/`);
    };

    const onPrintJobCompletionReport = (workscheduleId, event) => {
        event.preventDefault();
        navigate(`/JobCompletionReport/${workscheduleId}/`);
    };

    const onDeleteWorkSchedule = (workscheduleId, event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure to Delete?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                deleteWorkSchedule(cookies, workscheduleId)
                    .then(() => {
                        setRefreshKey((oldKey) => oldKey + 1);
                        Swal.fire('Deleted Successfully!', '', 'success');
                    })
                    .catch((error) => {
                        setRefreshKey((oldKey) => oldKey + 1);
                        displayError(error.response?.data, 'Delete Failed');
                    });
            } else if (result.isDismissed) {
                Swal.fire('Not Deleted', '', 'info');
            }
        });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((c) => c.headerName),
            data.records.map((ws) => ({
                plant: ws.plant,
                schedule_no: ws.schedule_no,
                delivery_date: ws.delivery_date,
                delivery_time: ws.delivery_time,
                customer: ws.customer,
                consignee: ws.consignee,
                scheduled_qty: ws.scheduled_qty,
                supplied_qty: ws.supplied_qty,
                accepted_qty: ws.accepted_qty,
                balance_qty: ws.balance_qty,
                grade: ws.grade,
                delivery_mode: ws.delivery_mode,
                salesrep_alias: ws.salesrep_alias,
                concrete_structure: ws.concrete_structure,
                status: ws.status,
                status_2: ws.status_2,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Schedules',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((ws) => ({
                plant: ws.plant,
                schedule_no: ws.schedule_no,
                delivery_date: ws.delivery_date,
                delivery_time: ws.delivery_time,
                customer: ws.customer,
                consignee: ws.consignee,
                scheduled_qty: ws.scheduled_qty,
                supplied_qty: ws.supplied_qty,
                accepted_qty: ws.accepted_qty,
                balance_qty: ws.balance_qty,
                grade: ws.grade,
                delivery_mode: ws.delivery_mode,
                salesrep_alias: ws.salesrep_alias,
                concrete_structure: ws.concrete_structure,
                status: ws.status,
                status_2: ws.status_2,
            })),
            'WorkScheduleList.pdf',
        );
    };

    const loadWorkScheduleList = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllWorkSchedules(cookies, buildWorkScheduleQueryParams(config))
            .then((workscheduleList) => {
                if (!workscheduleList?.work_schedule_list) {
                    throw new Error('Work schedule list was not returned by the server.');
                }
                const tableData = workscheduleList.work_schedule_list.map((ws) => ({
                    id: ws.id,
                    plant: ws.plant?.alias_name ?? '',
                    schedule_no: ws.schedule_no,
                    schedule_date: ws.schedule_date,
                    delivery_date: ws.delivery_date,
                    delivery_time: ws.delivery_time,
                    scheduled_qty: ws.scheduled_qty,
                    accepted_qty: ws.accepted_qty ?? 0,
                    supplied_qty: ws.supplied_qty ?? 0,
                    order_quantity: ws.sales_order_detail?.order_quantity ?? 0,
                    balance_qty: ws.balance_qty,
                    delivery_mode: (ws.delivery_mode ?? '').toUpperCase(),
                    order_no: ws.sales_order_detail?.order_no ?? '',
                    order_date: ws.sales_order_detail?.order_date ?? '',
                    consignee: ws.sales_order_detail?.consignee?.project_name ?? '',
                    customer: ws.sales_order_detail?.consignee?.customer?.name ?? '',
                    grade: ws.sales_order_detail?.grade ?? '',
                    concrete_structure: ws.sales_order_detail?.concrete_structure ?? '',
                    salesrep: ws.sales_order_detail?.consignee?.customer?.salesrep?.name ?? '',
                    salesrep_alias: ws.sales_order_detail?.consignee?.customer?.salesrep?.alias ?? '',
                    pump: ws.pump?.name ?? '',
                    status: ws.status,
                    status_2: ws.status_2 ? 'Active' : 'Inactive',
                    is_schedule_completed: ws.is_schedule_completed ? 'Yes' : 'No',
                    hide_row_edit: ws.is_schedule_completed ? 'Yes' : 'No',
                    hide_row_print: ws.status === 'completed' ? 'No' : 'Yes',
                }));

                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load work schedules');
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadWorkScheduleList(config);
    }, [loadWorkScheduleList]);

    useEffect(() => {
        getListViewSettings(cookies, 'work_schedule')
            .then((response) => {
                const config = response.config || {};
                loadWorkScheduleList(config);
            })
            .catch((error) => {
                displayError(error.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadWorkScheduleList, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/WorkSchedule"
                    addNewLabel="New Work Schedule"
                    homeLink="/Home"
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                />
                <div className="container">
                    <PremiumPageHeader
                        module="PLANNING"
                        title="List of Schedules"
                        subtitle="Configure filters in General Settings. The table loads based on your saved preferences."
                        modeLabel="VIEW MODE"
                        headerActions={
                            <ListViewSettings
                                viewKey="work_schedule"
                                onSaved={handleSettingsSaved}
                            />
                        }
                    />
                </div>

                <Box
                    className="container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        mb: 2.5,
                        py: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            flexShrink: 0,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: todaySchCard.gradient,
                                boxShadow: todaySchCard.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.68rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                    opacity: 0.9,
                                    lineHeight: 1.2,
                                }}
                            >
                                {todaySchCard.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '1.35rem',
                                    fontWeight: 700,
                                    lineHeight: 1.15,
                                    letterSpacing: '-0.02em',
                                    mt: 0.25,
                                }}
                            >
                                {todaySchCard.value}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 1.25,
                            flex: 1,
                            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        {summaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    flex: '0 0 auto',
                                    minWidth: 128,
                                    borderRadius: '12px',
                                    background: card.gradient,
                                    boxShadow: card.shadow,
                                    px: 1.75,
                                    py: 1.1,
                                    color: '#fff',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '0.68rem',
                                        fontWeight: 600,
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                        opacity: 0.9,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {card.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '1.35rem',
                                        fontWeight: 700,
                                        lineHeight: 1.15,
                                        letterSpacing: '-0.02em',
                                        mt: 0.25,
                                    }}
                                >
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF SCHEDULES"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditWorkSchedule}
                            onRowDelete={onDeleteWorkSchedule}
                            onRowPrint={onPrintJobCompletionReport}
                            hidePrint={false}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactRows={true}
                        />
                    </div>
                </div>
            </div>
            <button
                style={{ position: 'relative', right: '40px' }}
                className="btn btn-fill btn-primary float-end"
                type="button"
                onClick={() => { navigate('/WorkSchedule'); }}
            >
                Back
            </button>
        </div>
    );
}

export default WorkScheduleTable;
